import { take, call, put, race, all } from 'redux-saga/effects'
import { delay } from 'redux-saga'

import * as api from '../services/api'
import { TimeoutException } from '../services/exceptions'
import { actions as appActions } from './appModule'
import { ALERTS, COMPANY_TYPE, FTSE_INDEX, MONITORING, NEWS_CATEGORY } from "../modules/constants"

// Set default value for props that are not functions (eg NEWS_CATEGORY.getName())
const setUserAlertsDefaults = (myConst, key, value) => {
  if (typeof myConst[key] !== "function")
    userAlertsDefaults[myConst[key]] = value
}

// Init 'userAlerts' from NEWS_CATEGORY
const userAlertsDefaults = {
  [ALERTS.NEWS]: false,
}
// console.log('userAlertsDefaults:', userAlertsDefaults)

// Default 1st item to true (index === 0 )and remainder to false (index !== 0)
Object.keys(NEWS_CATEGORY).forEach((key, index) => setUserAlertsDefaults(NEWS_CATEGORY, key, index === 0))
Object.keys(FTSE_INDEX).forEach((key, index) => setUserAlertsDefaults(FTSE_INDEX, key, index === 0))
Object.keys(COMPANY_TYPE).forEach((key, index) => setUserAlertsDefaults(COMPANY_TYPE, key, true))
// console.log('userAlertsDefaults:', userAlertsDefaults)
// console.log('NEWS_CATEGORY:',NEWS_CATEGORY)


const setUserMonitoringDefaults = (myConst, key, value) => {
  if (typeof myConst[key] !== "function")
    userMonitoringDefaults[myConst[key]] = value
}

// Init 'userMonitoring' from MONITORING
const userMonitoringDefaults = {
  // [MONITORING.DISCLOSURES]: false,
}
Object.keys(MONITORING.FTSE_INDEX).forEach((key, index) => setUserMonitoringDefaults(MONITORING.FTSE_INDEX, key, index === 0))
// console.log('userMonitoringDefaults:',userMonitoringDefaults)

// Initialise the monitoring search terms
const initMonitoringSearchTerms = () => {
  const searchTerms = Array(MONITORING.NUM_SEARCH_TERMS).fill('')
  // searchTerms[0] = "strategic review"
  return searchTerms
}

const initialState = {
  loaded: false,
  importing: false,
  importError: undefined,
  sourceIds: {},
  isDemoCompanys: {},
  orgNames: {},
  companyExists: false,
  companyName: undefined,
  previousSearchString: "",
  searchResults: {
    items: [],
    totalResults: undefined
  },
  userMonitoringSearching: false,
  userMonitoring: {
    Categories: null,
    Enabled: false,
    SearchTerms: initMonitoringSearchTerms(),
  },
}

export function reducer(state = initialState, action) {
  switch (action.type) {

    case 'user.changeSize': {
      return { ...state, portrait: (window.innerHeight > window.innerWidth) }
    }

    case 'user.load.start': {
      return { ...state }
    }

    case 'user.save': {
      return { ...state, ...action.user }
    }

    case 'user.updateUserAlertsConfig': {
      // console.log('updateUserAlertsConfig action:', action)
      return { ...state, userAlerts: action.userAlerts }
    }

    case 'user.updateUserMonitoringConfig': {
      // console.log('updateUserMonitoringConfig action:', action)
      return { ...state, userMonitoring: action.userMonitoring }
    }

    case 'user.monitoringTest': {
      // console.log('monitoringTest action:', action)

      // A single Test click can still produce more than one server response reaching the
      // browser (the backend lock stops a duplicate from running the search/sending a second
      // email, but a duplicate call can still resolve and land here after the real one). Once
      // a genuine result has been shown for the current test, ignore a later stray error rather
      // than overwriting it - a real new click always resets state first (test: true, below),
      // so it's unaffected by this and still shows its own result normally.
      if (action.test === false && action.error && state.userMonitoringSearching === false)
        return state

      return {
        ...state,
        userMonitoringSearching: action.test,
        userMonitoringResults: action.results,
        userMonitoringError: action.error ?? null,
        userMonitoringMaxDocumentsExceeded: action.maxDocumentsExceeded ?? false,
        userMonitoringSynopsis: action.synopsis ?? null,
        userMonitoringGeminiUsage: action.geminiUsage ?? null,
        // action.test === true is the click itself (see handleTest in MonitoringConfigPage) - the
        // backend records the same moment against the user (as Monitoring.Config.LastTestRun) to
        // enforce its own cooldown lock, so mirroring it here client-side keeps the Test button's
        // disabled/countdown state accurate without waiting for the (potentially ~45s) response
        ...(action.test === true ? { userMonitoring: { ...state.userMonitoring, LastTestRun: new Date().toISOString() } } : {}),
      }
    }

    default: {
      return state
    }
  }
}

export const actions = {
  changeSize: () => ({ type: 'user.changeSize' }),
  load: () => ({ type: 'user.load.start' }),
  saveUser: user => ({ type: 'user.save', user }),
  setUserAlertsConfig: userAlerts => ({ type: 'user.setUserAlertsConfig', userAlerts }),
  updateUserAlertsConfig: userAlerts => ({ type: 'user.updateUserAlertsConfig', userAlerts }),
  getUserAlertsConfig: () => ({ type: 'user.getUserAlertsConfig' }),

  setUserMonitoringConfig: userMonitoring => ({ type: 'user.setUserMonitoringConfig', userMonitoring }),
  updateUserMonitoringConfig: userMonitoring => ({ type: 'user.updateUserMonitoringConfig', userMonitoring }),
  getUserMonitoringConfig: () => ({ type: "user.getUserMonitoringConfig" }),

  saveSearchTerms: props => ({ props, type: 'user.saveSearchTerms' }),
  monitoringTest: props => ({ ...props, type: 'user.monitoringTest' }),
}

export const selectors = {
  isLoaded: state => state.loaded === true,
  isImporting: state => state.importing,
  getId: state => state.id,
  getName: state => state.name,
  getTrialDaysRemaining: state => state.standardTrialDaysLeft,
  getEmail: state => state.email,
}

function* load() {
  try {
    const { json, timeout } = yield race({
      json: call(api.getMe),
      timeout: call(delay, 15 * 1000)
    })

    if (timeout) {
      throw new TimeoutException('User request timed out.')
    }

    yield put(actions.saveUser(json))

    return false
  } catch (e) {
    if (e.name === 'AuthenticationException') {
      yield put(appActions.logout())
      return false
    } else {
      return true
    }
  }
}

function* watchLoadUser() {
  while (yield take('user.load.start')) {
    while (yield load()) {
      yield call(delay, 1000)
    }
  }
}

//#region watch UserAlerts
function* watchSetUserAlertsConfig() {
  while (true) {
    const action = yield take('user.setUserAlertsConfig')
    // console.log('user.setUserAlertsConfig action:', action)
    let userAlerts = yield call(api.favourites.setUserAlertsConfig, action.userAlerts)
    // console.log('userAlerts:', userAlerts)
    yield put(actions.updateUserAlertsConfig(userAlerts))
  }
}

function* watchGetUserAlertsConfig() {
  while (true) {
    yield take('user.getUserAlertsConfig')
    // console.log('user.getUserAlertsConfig action:')
    let userAlerts = (yield call(api.favourites.getUserAlertsConfig)) ?? {}
    // console.log('userAlerts:', userAlerts)
    addMissingDefaults(userAlerts, userAlertsDefaults)
    yield put(actions.updateUserAlertsConfig(userAlerts))
  }
}
//#endregion

//#region watch UserMonitoring
function* watchSetUserMonitoringConfig() {
  while (true) {
    const { userMonitoring } = yield take('user.setUserMonitoringConfig')
    // console.log('user.setUserMonitoringConfig userMonitoring:', userMonitoring)
    yield call(api.monitoring.setUserMonitoringConfig, userMonitoring)
    yield put(actions.updateUserMonitoringConfig(userMonitoring))
  }
}

function* watchGetUserMonitoringConfig() {
  while (true) {
    yield take('user.getUserMonitoringConfig')
    // console.log('user.getUserMonitoringConfig action:')
    const userMonitoring = yield call(api.monitoring.getUserMonitoringConfig)

    addMissingDefaults(userMonitoring.Categories, userMonitoringDefaults)

    if (userMonitoring.SearchTerms === null)
      userMonitoring.SearchTerms = initMonitoringSearchTerms()

    yield put(actions.updateUserMonitoringConfig(userMonitoring))
  }
}

// Add default value for any props missing from userAlerts
const addMissingDefaults = (objects, defaults) => {
  // console.log('addMissingDefaults:',objects,', ', defaults)
  for (const [key, value] of Object.entries(defaults))
    if (objects[key] === undefined)
      objects[key] = value
}

function* watchSaveUserMonitoringSearchTerms() {
  while (true) {
    const { props } = yield take('user.saveSearchTerms')
    // console.log('user.saveSearchTerms: props:', props)
    const { userMonitoring, searchTerms } = props
    userMonitoring.SearchTerms = searchTerms
    yield put(actions.setUserMonitoringConfig(userMonitoring))
  }
}

function* watchMonitoringTest() {
  while (true) {
    yield take('user.monitoringTest')
    // console.log('user.monitoringTest')
    try {
      let response = yield call(api.monitoring.monitoringTest)
      // console.log('response:', response)
      yield put(actions.monitoringTest({ results: response.Releases, maxDocumentsExceeded: response.MaxDocumentsExceeded, synopsis: response.Synopsis, geminiUsage: response.GeminiUsage, test: false }))
    } catch (e) {
      yield put(actions.monitoringTest({ results: [], test: false, error: e.message }))
    }
  }
}
//#endregion watch UserMonitoring

export function* sagas() {
  yield all([
    watchLoadUser(),

    watchSetUserAlertsConfig(),
    watchGetUserAlertsConfig(),

    watchSetUserMonitoringConfig(),
    watchGetUserMonitoringConfig(),
    watchSaveUserMonitoringSearchTerms(),

    watchMonitoringTest(),
  ])
}
